"use client";

import { cn, formatPrice, isSaleProduct } from "@/lib/utils";
import Image from "next/image";
import { StoreCartLineItem, StoreOrderLineItem, StoreProduct } from "@medusajs/types";
import { ProductDocument } from "@/prismicio-types";
import { useState } from "react";
import { Locales } from "@/lib/i18n";
import CartProductItem from "@/components/organisms/cart-product-item";
import { __, _x } from "@/lib/i18n/functions";
import ProductBadge from "@/components/atoms/product-badge";
import { formatProductQuantity } from "@/lib/product-quantity-label";
import { sanitizeImageUrl } from "@/components/molecules/image-gallery";
import { PriceToastDropdown } from "@/components/atoms/price-toast-dropdown";
import { Badge } from "@/components/ui/badge";
import ProductConfigurationModal from "@/components/modules/account/product-configuration-modal";
import {
  normalizeConditioning,
  resolveProductConditioning,
  toLotPrice,
  toLotQuantity,
  toPiecePrice,
} from "@/lib/product-units";

type ProductSummaryCardProps = {
  variant?: "default" | "secondary";
  medusaProduct: StoreProduct;
  prismicProduct?: ProductDocument;
  locale: Locales;
  className?: string;
  hideControls?: boolean;
  orderQuantity?: number;
} & ({ cartItem: StoreCartLineItem; orderItem?: never } | { orderItem: StoreOrderLineItem; cartItem?: never });

const ProductSummaryCard = ({
  variant,
  medusaProduct,
  prismicProduct,
  locale,
  className,
  hideControls = false,
  ...props
}: ProductSummaryCardProps) => {
  const item = props.cartItem || props.orderItem;
  const [count, setCount] = useState(item?.quantity || 1);

  if (!item) {
    return null;
  }

  // Product metadata
  const productConditioning = resolveProductConditioning(
    medusaProduct?.metadata?.conditioning,
    medusaProduct?.metadata?.conditioning_bac
  );
  const physicalPieceCount = normalizeConditioning(productConditioning);
  const isFullImage = prismicProduct?.data.image_full_size_card || false;
  const isRange = medusaProduct?.metadata?.is_surface_based as boolean;
  const isPriceDynamic = medusaProduct?.metadata?.is_price_dynamic as boolean;
  const hasMultipleVariants = (medusaProduct?.variants?.length ?? 0) > 1;

  // Variant from cart/order (ground truth)
  // For single-variant products we always use variants[0].
  const lineItemVariantId = props.cartItem?.variant_id ?? props.orderItem?.variant_id;
  const cartVariant = hasMultipleVariants
    ? medusaProduct?.variants?.find((v) => v.id === lineItemVariantId)
    : medusaProduct?.variants?.[0];
  const cartVariantSurface = cartVariant?.metadata?.["surface"] as number | undefined;

  const cartVariantPrice = cartVariant?.calculated_price?.calculated_amount ?? 0;
  const singleVariantPrice = medusaProduct?.variants?.[0]?.calculated_price?.calculated_amount || 0;
  const productPrice = hasMultipleVariants ? cartVariantPrice : singleVariantPrice;
  const pricePerPiece = toPiecePrice(productPrice, productConditioning);
  const lotQuantity = toLotQuantity(item.quantity, productConditioning);

  const value = formatPrice(toLotPrice(singleVariantPrice, productConditioning));

  // Formatted prices (used in desktop list item)
  const formattedVariantUnitPrice = hasMultipleVariants
    ? formatPrice(toLotPrice(cartVariantPrice, productConditioning), { showDecimals: true })
    : value;

  const formattedVariantTotalPrice = hasMultipleVariants
    ? formatPrice(cartVariantPrice * count, { showDecimals: true })
    : formatPrice(singleVariantPrice * count);

  // Badge label for "Price per unit" when count > 1
  const selectedVariantPriceBadge = hasMultipleVariants ? formattedVariantUnitPrice : value;

  return (
    <>
      <li className={cn("flex items-center gap-5.5 max-sm:hidden", className)}>
        <figure className="bg-product-card relative flex size-[74px] shrink-0 items-center justify-center overflow-hidden rounded-md">
          {isFullImage ? (
            <Image
              src={
                sanitizeImageUrl(medusaProduct?.images?.at(0)?.url || "") ||
                prismicProduct?.data?.images?.[0]?.image?.url ||
                prismicProduct?.data?.featuredImage?.url ||
                ""
              }
              alt={prismicProduct?.data?.title || medusaProduct?.title}
              fill
              className="absolute object-cover mix-blend-multiply"
            />
          ) : (
            <div className="relative size-[52px]">
              <Image
                src={
                  sanitizeImageUrl(medusaProduct?.images?.at(0)?.url || "") ||
                  prismicProduct?.data?.images?.[0]?.image?.url ||
                  prismicProduct?.data?.featuredImage?.url ||
                  ""
                }
                alt={prismicProduct?.data?.title || medusaProduct?.title}
                fill
                className="absolute object-contain mix-blend-multiply"
              />
            </div>
          )}
        </figure>

        <dl className="flex w-full items-center justify-between gap-6">
          <div className="space-y-2">
            <dt className="sr-only">Product Name</dt>
            <dd>
              <h3 className="font-cabinet-grotesk leading-h1 text-xl font-semibold">
                {prismicProduct?.data?.title || medusaProduct?.title}
              </h3>
            </dd>
            <div className="flex items-center gap-2">
              {isSaleProduct(medusaProduct) && <Badge variant="festi">{__("To sell", locale)}</Badge>}
              {hasMultipleVariants && isSaleProduct(medusaProduct) && count > 1 && (
                <ProductBadge
                  text={_x("Price : {{ selectedVariantPriceBadge }}", { selectedVariantPriceBadge }, locale)}
                />
              )}
            </div>
            {isRange && !hideControls && (
              <ProductConfigurationModal
                products={[prismicProduct!]}
                medusaProducts={{ [medusaProduct.id]: medusaProduct }}
                locale={locale}
                onlyIcon
                defaultSurface={cartVariantSurface}
                hideWhenConfigured
              />
            )}
            <div className="leading-body-md text-primary-light flex items-center gap-1 text-sm">
              {variant === "default" && (
                <>
                  <dt>{__("Quantity", locale)} :</dt>
                  <dd>
                    {toLotQuantity(item.quantity, productConditioning)} {(physicalPieceCount > 1 || count > 1) && !isRange && !isPriceDynamic && "|"}{" "}
                  </dd>
                </>
              )}
              {count > 1 && !isRange && !isPriceDynamic && (
                <>{<ProductBadge text={_x("Price : {{ value }}", { value }, locale)} />}</>
              )}
              {physicalPieceCount > 1 && (
                <>
                  {variant === "secondary" && (
                    <>
                      <dt className="sr-only">Per lot</dt>
                      <ProductBadge
                        text={`${lotQuantity} ${lotQuantity === 1 ? __("batch", locale) : __("batches", locale)}`}
                      />
                    </>
                  )}
                  <dt className="sr-only">Price per piece</dt>
                  <dd>
                    {formatPrice(pricePerPiece, {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                    /{__("piece", locale)}
                  </dd>
                </>
              )}
            </div>
          </div>

          <div className="flex w-20 flex-col items-end gap-1">
            {isRange || isPriceDynamic ? (
              hasMultipleVariants ? (
                // Price derived from the cart/order variant — store is never consulted
                <p className="text-xl font-medium">{formattedVariantTotalPrice}</p>
              ) : (
                <PriceToastDropdown locale={locale} />
              )
            ) : (
              <>
                {physicalPieceCount > 1 && (
                  <ProductBadge className="max-w-full whitespace-normal text-right" text={formatProductQuantity(count, productConditioning, locale)} />
                )}
                <dd className="sr-only">Price</dd>
                <dt className="text-xl font-medium">
                  {formatPrice((medusaProduct.variants?.at(0)?.calculated_price?.calculated_amount || 0) * count)}
                </dt>
              </>
            )}
            {variant === "secondary" && productConditioning === 1 && (
              <>
                <dd className="sr-only">Quantity</dd>
                <dt className="text-primary-light min-w-22 text-right text-lg">
                  {item.quantity} {item.quantity > 1 ? __("items", locale) : __("item", locale)}
                </dt>
              </>
            )}
          </div>
        </dl>
      </li>

      {props.cartItem ? (
        <CartProductItem
          locale={locale}
          medusaProduct={medusaProduct}
          prismicProduct={prismicProduct}
          cartItem={props.cartItem}
          count={count}
          setCount={setCount}
          className="sm:hidden"
          hideControls={hideControls}
          orderQuantity={item.quantity}
          isFullImage={isFullImage}
        />
      ) : props.orderItem ? (
        <CartProductItem
          locale={locale}
          medusaProduct={medusaProduct}
          prismicProduct={prismicProduct}
          orderItem={props.orderItem}
          count={count}
          setCount={setCount}
          className="sm:hidden"
          hideControls={hideControls}
          orderQuantity={item.quantity}
          isFullImage={isFullImage}
        />
      ) : null}
    </>
  );
};

export default ProductSummaryCard;
